import type { ContactStatusType, SequenceLogEntry } from '@components/ProfileCard';
import type { Platform } from '@components/PlatformIcon';
import type { SocialProvider } from '@components/SocialIcon';
import { flavorConfig } from '@config/config';
import type { ApolloEventDoc, Lead } from '@services/firebase/firestore';

export const TOTAL_SEQUENCE_EMAILS = 3;

// ---------------------------------------------------------------------------
// Static filter option arrays
// ---------------------------------------------------------------------------

export const technologyOptions = [
  { value: 'framer', label: 'Framer' },
  { value: 'shopify', label: 'Shopify' },
  { value: 'magento', label: 'Magento' },
  { value: 'woocommerce', label: 'WooCommerce' },
  { value: 'wordpress', label: 'WordPress' },
  { value: 'unknown', label: 'Unknown' },
];

export const contactInfoOptions = [
  { value: 'all-contacts', label: 'All Contacts' },
  { value: 'has-email', label: 'Has Email' },
  { value: 'has-whatsapp', label: 'Has WhatsApp' },
  { value: 'no-contacts', label: 'No Contacts' },
];

export const leadStatusOptions = [
  // Onboarding lifecycle
  { value: 'not-onboarded', label: 'Not Onboarded', group: 'Onboarding' },
  { value: 'onboarded', label: 'Onboarded', group: 'Onboarding' },
  { value: 'onboarded-by-me', label: 'Onboarded by Me', group: 'Onboarding' },
  // Manual triage / rating
  { value: 'marked-ok', label: 'Marked OK', group: 'Triage' },
  { value: 'marked-not-ok', label: 'Marked Not OK', group: 'Triage' },
  { value: 'unmarked', label: 'Unmarked', group: 'Triage' },
  // Active outreach work
  { value: 'not-contacted', label: 'Not Contacted', group: 'Nurturing' },
  { value: 'contacted', label: 'Contacted', group: 'Nurturing' },
  { value: 'not-enriched', label: 'Not Enriched', group: 'Nurturing' },
  { value: 'enriched', label: 'Enriched', group: 'Nurturing' },
  // Terminal states (hidden by default per SC-1450 archive-model)
  { value: 'converted', label: 'Converted', group: 'Completed' },
  { value: 'lost', label: 'Lost', group: 'Completed' },
];

// ---------------------------------------------------------------------------
// Pure helper functions
// ---------------------------------------------------------------------------

/** Derive the technology platform from a lead's site-explorer data. */
export const getPlatform = (lead: Lead): Platform => {
  const explorer = lead['site-explorer'];
  if (explorer?.isFramer) return 'framer';
  if (explorer?.isShopify) return 'shopify';
  if (explorer?.isMagento) return 'magento';
  if (explorer?.isWoocommerce) return 'woocommerce';
  if (explorer?.isWordpress) return 'wordpress';
  return 'unknown';
};

/** Build the demo-site URL for an onboarded lead. */
export const getDemoSiteUrl = (siteId: string): string =>
  `${flavorConfig.demoSiteBaseUrl}/demo/${siteId}`;

export interface TransferLinkInputs {
  siteId: string;
  firstName: string;
  email: string;
  onboardedBy: string;
  siteUrl: string;
  transferCode: string;
}

/**
 * Build a personalised demo-transfer link for an enriched contact.
 *
 * Format:
 *   {demoSiteBaseUrl}/demo/{siteId}
 *     ?n={firstName}&e={email}
 *     &utm_campaign={onboardedBy}&utm_medium={siteUrl}&utm_source=email
 *     &t={transferCode}
 *
 * All dynamic values are URL-encoded. `utm_source` is hardcoded to `email`.
 * The env-aware prefix (dev/qa/prod) comes from `flavorConfig.demoSiteBaseUrl`.
 *
 * Returns `null` when any required field is missing or empty, so callers can
 * cleanly omit the button.
 */
export const buildTransferLinkUrl = (inputs: TransferLinkInputs): string | null => {
  const { siteId, firstName, email, onboardedBy, siteUrl, transferCode } = inputs;
  if (!siteId || !firstName || !email || !onboardedBy || !siteUrl || !transferCode) {
    return null;
  }
  const params = new URLSearchParams({
    n: firstName,
    e: email,
    utm_campaign: onboardedBy,
    utm_medium: siteUrl,
    utm_source: 'email',
    t: transferCode,
  });
  return `${flavorConfig.demoSiteBaseUrl}/demo/${encodeURIComponent(siteId)}?${params.toString()}`;
};

/**
 * Derive contact status, progress, and sequence log from apollo_events data.
 * Pure function — safe to call in render paths or unit tests.
 */
export const deriveContactState = (
  contactId: string | null | undefined,
  eventsMap: Map<string, ApolloEventDoc>
): {
  contactStatus: ContactStatusType;
  contactProgress: { current: number; total: number };
  sequenceLog: SequenceLogEntry[];
} => {
  const defaultResult = {
    contactStatus: 'in-progress' as ContactStatusType,
    contactProgress: { current: 0, total: TOTAL_SEQUENCE_EMAILS },
    sequenceLog: [] as SequenceLogEntry[],
  };

  if (!contactId) return defaultResult;

  const eventDoc = eventsMap.get(contactId);
  if (!eventDoc?.emails) return defaultResult;

  const emailEntries = Object.values(eventDoc.emails);
  const eventCount = emailEntries.length;

  // Sort by due_at ascending
  const sorted = [...emailEntries].sort((a, b) => {
    const aTime = a.due_at ? new Date(a.due_at).getTime() : 0;
    const bTime = b.due_at ? new Date(b.due_at).getTime() : 0;
    return aTime - bTime;
  });

  // Build sequence log entries
  const sequenceLog: SequenceLogEntry[] = sorted.map((email) => {
    let sendTime = '';
    if (email.due_at) {
      const date = new Date(email.due_at);
      sendTime = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }

    const status = email.status
      ? email.status.charAt(0).toUpperCase() + email.status.slice(1)
      : '';

    return {
      sendTime,
      status,
      subject: email.subject || '',
    };
  });

  // Completed only when ALL events have status "completed"
  const allCompleted = eventCount > 0 && emailEntries.every((e) => e.status === 'completed');
  const contactStatus: ContactStatusType = allCompleted ? 'completed' : 'in-progress';

  return {
    contactStatus,
    contactProgress: { current: eventCount, total: TOTAL_SEQUENCE_EMAILS },
    sequenceLog,
  };
};

/** Extract the WhatsApp contact from a lead, or null if not present. */
export const getWhatsAppFromLead = (
  lead: Lead
): { number: string; href: string } | null => {
  if (lead.contacts?.whatsappNumber && lead.contacts.whatsappNumber !== '') {
    return {
      number: lead.contacts.whatsappNumber,
      href: `https://wa.me/${lead.contacts.whatsappNumber.replace(/[^0-9]/g, '')}`,
    };
  }
  return null;
};

/** Transform the lead's social contacts into ContactLink-compatible objects (excludes WhatsApp). */
export const getContactsFromLead = (
  lead: Lead
): Array<{ id: string; provider: SocialProvider; label: string; href: string }> => {
  const contacts: Array<{ id: string; provider: SocialProvider; label: string; href: string }> =
    [];

  if (lead.contacts?.email) {
    contacts.push({
      id: `email-${lead.id}`,
      provider: 'email',
      label: lead.contacts.email,
      href: `mailto:${lead.contacts.email}`,
    });
  }

  if (!lead.contacts?.socials) return contacts;

  const { socials } = lead.contacts;

  if (socials.facebook) {
    contacts.push({
      id: `facebook-${lead.id}`,
      provider: 'facebook',
      label: socials.facebook,
      href: `https://facebook.com/${socials.facebook}`,
    });
  }

  if (socials.instagram) {
    contacts.push({
      id: `instagram-${lead.id}`,
      provider: 'instagram',
      label: socials.instagram,
      href: `https://instagram.com/${socials.instagram}`,
    });
  }

  if (socials.linkedin) {
    contacts.push({
      id: `linkedin-${lead.id}`,
      provider: 'linkedin',
      label: socials.linkedin,
      href: `https://linkedin.com/in/${socials.linkedin}`,
    });
  }

  if (socials.tiktok) {
    contacts.push({
      id: `tiktok-${lead.id}`,
      provider: 'tiktok',
      label: socials.tiktok,
      href: `https://tiktok.com/@${socials.tiktok}`,
    });
  }

  if (socials.twitter) {
    contacts.push({
      id: `x-${lead.id}`,
      provider: 'x',
      label: socials.twitter,
      href: `https://x.com/${socials.twitter}`,
    });
  }

  if (socials.youtube) {
    contacts.push({
      id: `youtube-${lead.id}`,
      provider: 'youtube',
      label: socials.youtube,
      href: `https://youtube.com/@${socials.youtube}`,
    });
  }

  return contacts;
};
